import React from 'react';
import { Detail, InboundLine } from './inboundApi';

// The ONE lines list of an inbound (Fernando 30.10, first real inbound): the inbound page and the scanning both show
// it — WGR's shape, which he likes better than a table: one white CARD per product on the light grey, a clear gap
// between products, one row per size inside (sizes small → big, d.groups / backend/sizes.py), a single column header
// on top. Columns are a CSS grid shared by the header and every card so they line up without a <table>. Light like
// the picking screen (CartApp's palette). The scanning passes the row refs (to scroll the scanned product to the
// middle) and the line to flash. -- Claude, 2026-10-30

const COLS = (shelf: boolean) => shelf
  ? 'grid grid-cols-[3.5rem_minmax(9rem,1fr)_8.5rem_minmax(12rem,2fr)_6rem_5rem_6.5rem_6rem_5rem] items-center'
  : 'grid grid-cols-[3.5rem_minmax(9rem,1fr)_8.5rem_minmax(12rem,2fr)_6rem_5rem_6.5rem] items-center';

/** the row's tint: scanned = green, short or over or extra = red, part = amber (light tints) */
function tone(l: InboundLine): string {
  const total = l.total_scanned;
  if (l.short) return 'bg-red-100';
  if (l.kind === 'extra') return total > 0 ? 'bg-red-100' : '';
  if (total === 0) return '';
  if (total > l.qty_ordered) return 'bg-red-100';
  if (total === l.qty_ordered) return 'bg-green-100';
  return 'bg-amber-100';
}

export default function LinesTable({ d, showShelf, flashLine, rowRefs, onUnscan, onEditBarcode, onVerify }: {
  d: Detail; showShelf?: boolean; flashLine?: number | null;
  /** filled with `l-<lineId>` → row element, for scrolling */
  rowRefs?: React.MutableRefObject<Map<string, HTMLElement>>;
  /** take one scanned piece back from this line (the −1; the server decides who may) */
  onUnscan?: (l: InboundLine) => void;
  /** the EAN cell is a button: fix this line's product barcode (the server's gate decides who may) */
  onEditBarcode?: (l: InboundLine) => void;
  /** "Checked — N came" on a line with more than ordered, or not on the order (1.11) */
  onVerify?: (l: InboundLine) => void;
}) {
  const lineById = new Map(d.lines.map(l => [l.id, l]));
  const ref = (key: string) => (el: HTMLElement | null) => { if (el && rowRefs) rowRefs.current.set(key, el); };
  const cols = COLS(!!showShelf);
  return (
    <div className="text-sm text-[#111111]">
      <div className={`${cols} px-3 py-1 text-[11px] uppercase tracking-wider text-[#5f6368]`}>
        <div>Box</div><div>Article</div><div>EAN</div><div>Product</div><div>Size</div><div className="text-right">Ordered</div><div className="text-right">Scanned</div>
        {showShelf && <div>Shelf</div>}{showShelf && <div className="text-right">Shelved</div>}
      </div>
      {d.groups.map(g => (
        <div key={g.style_key} className="bg-white border border-[#9aa0a6] rounded-xl mb-4 overflow-hidden">
          {g.line_ids.map(id => lineById.get(id)).filter((l): l is InboundLine => !!l).map((l, li) => (
            <div key={l.id} ref={ref(`l-${l.id}`)}
              className={`${cols} px-3 py-2 ${li > 0 ? 'border-t border-[#e5e7eb]' : ''} ${tone(l)} ${flashLine === l.id ? 'outline outline-2 outline-[#111111] -outline-offset-2' : ''}`}>
              <div className="font-bold text-lg">{l.box_label ?? ''}</div>
              <div>{l.article_number}{l.kind === 'extra' && <span className="ml-2 text-[10px] uppercase text-red-700">{l.product_ref ? 'extra' : 'unknown'}</span>}{(l.waiting || 0) > 0 && <span className="ml-2 text-[10px] font-semibold px-1 rounded bg-sky-100 text-sky-900" title={`${l.waiting} piece(s) of this size are sold — orders wait in Waiting for NOS-product`}>W-F-NOS ×{l.waiting}</span>}</div>
              <div className="text-[#374151] tabular-nums">
                {onEditBarcode && l.kind === 'ordered'
                  ? <button onClick={() => onEditBarcode(l)} title="fix this product's barcode (a wrong or missing one)" className="hover:underline hover:text-blue-700 text-left">{l.ean || <span className="text-[#9aa0a6]">+ barcode</span>}</button>
                  : (l.ean || '')}
              </div>
              <div className="text-[#374151] truncate" title={l.name || ''}>{l.name}</div>
              <div className="font-semibold">{l.size}</div>
              <div className="text-right text-[#5f6368]">{l.qty_ordered}</div>
              <div className="text-right font-semibold whitespace-nowrap">{l.total_scanned}{l.ongoing > 0 && <span className="text-[#5f6368] text-xs font-normal"> (+{l.ongoing})</span>}
                {onUnscan && l.total_scanned > 0 && (
                  <button onClick={() => onUnscan(l)} title="take one scanned piece back (a double scan, a wrong barcode)"
                    className="ml-1.5 px-1.5 rounded border border-[#9aa0a6] text-xs font-semibold text-[#5f6368] hover:bg-red-100 hover:text-red-800">−1</button>
                )}
                {onVerify && l.needs_check && (
                  <button onClick={() => onVerify(l)} title="the count is right — the pieces may go on a cart"
                    className="ml-1.5 px-1.5 rounded bg-amber-300 hover:bg-amber-400 text-xs font-semibold text-black">Checked — {l.total_scanned} came</button>
                )}
                {!l.needs_check && l.verified_by && (l.kind === 'extra' || l.total_scanned > l.qty_ordered) && (
                  <span className="ml-1.5 text-[10px] text-[#5f6368]" title={`checked by ${l.verified_by}`}>✓ checked</span>
                )}
              </div>
              {showShelf && <div className="text-[#374151]">{l.stock_location || '—'}</div>}
              {showShelf && <div className="text-right text-[#374151]">{l.qty_replenished}</div>}
            </div>
          ))}
        </div>
      ))}
      {d.lines.length === 0 && <div className="py-3 text-[#5f6368]">No lines.</div>}
    </div>
  );
}
